import React, { useState, useRef, useEffect } from 'react';
import { Group } from '@services/GroupService';
import './BulkUploadRelationshipsModal.css';

interface Assessment {
  assessmentId: number;
  assessmentTitle: string;
  status: string;
}

interface BulkUploadRelationshipsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BulkRelationshipData[], assessmentId?: number, groupId?: number) => Promise<void>;
  assessments?: Assessment[];
  groups?: Group[];
  onCreateGroup?: (name: string) => Promise<Group | null>;
  defaultAssessmentId?: number;
}

export interface BulkRelationshipData {
  employee_name: string;
  employee_email: string;
  manager_name: string;
  manager_email: string;
}

const BulkUploadRelationshipsModal: React.FC<BulkUploadRelationshipsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  assessments = [],
  groups = [],
  onCreateGroup,
  defaultAssessmentId
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<BulkRelationshipData[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<number | undefined>(undefined);
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined);
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize assessment dropdown with default when modal opens
  useEffect(() => {
    if (isOpen && defaultAssessmentId) {
      setSelectedAssessmentId(defaultAssessmentId);
    }
  }, [isOpen, defaultAssessmentId]);

  const readyAssessments = assessments.filter(a => a.status === 'ready');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const parseCSV = (text: string): BulkRelationshipData[] => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    if (lines.length < 2) {
      throw new Error('CSV file must contain at least a header row and one data row');
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().toLowerCase());
    const expectedHeaders = ['employee_name', 'employee_email', 'manager_name', 'manager_email'];

    // Validate headers
    const hasAllHeaders = expectedHeaders.every(h => header.includes(h));
    if (!hasAllHeaders) {
      throw new Error(`CSV must contain columns: ${expectedHeaders.join(', ')}`);
    }

    // Get column indices
    const employeeNameIdx = header.indexOf('employee_name');
    const employeeEmailIdx = header.indexOf('employee_email');
    const managerNameIdx = header.indexOf('manager_name');
    const managerEmailIdx = header.indexOf('manager_email');

    // Parse data rows
    const data: BulkRelationshipData[] = [];
    const parseErrors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const values = line.split(',').map(v => v.trim());

      if (values.length < 4) {
        parseErrors.push(`Row ${i + 1}: Insufficient columns`);
        continue;
      }

      const employeeName = values[employeeNameIdx];
      const employeeEmail = values[employeeEmailIdx];
      const managerName = values[managerNameIdx];
      const managerEmail = values[managerEmailIdx];

      // Validate data
      if (!employeeName) {
        parseErrors.push(`Row ${i + 1}: Employee name is required`);
        continue;
      }
      if (!employeeEmail || !validateEmail(employeeEmail)) {
        parseErrors.push(`Row ${i + 1}: Invalid employee email: ${employeeEmail}`);
        continue;
      }
      if (!managerName) {
        parseErrors.push(`Row ${i + 1}: Manager name is required`);
        continue;
      }
      if (!managerEmail || !validateEmail(managerEmail)) {
        parseErrors.push(`Row ${i + 1}: Invalid manager email: ${managerEmail}`);
        continue;
      }

      data.push({
        employee_name: employeeName,
        employee_email: employeeEmail,
        manager_name: managerName,
        manager_email: managerEmail
      });
    }

    if (parseErrors.length > 0) {
      setErrors(parseErrors);
    }

    return data;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setErrors(['Please select a valid CSV file']);
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setParsedData([]);

    // Read and parse the file
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const data = parseCSV(text);
        setParsedData(data);
      } catch (error: any) {
        setErrors([error.message || 'Failed to parse CSV file']);
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || !onCreateGroup) return;

    setCreatingGroup(true);
    try {
      const newGroup = await onCreateGroup(newGroupName.trim());
      if (newGroup) {
        setSelectedGroupId(newGroup.id);
        setShowNewGroupInput(false);
        setNewGroupName('');
      }
    } catch (error: any) {
      setErrors([error.message || 'Failed to create group']);
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleGroupChange = (value: string) => {
    if (value === 'new') {
      setShowNewGroupInput(true);
      setSelectedGroupId(undefined);
    } else {
      setShowNewGroupInput(false);
      setNewGroupName('');
      setSelectedGroupId(value ? parseInt(value) : undefined);
    }
  };

  const handleSubmit = async () => {
    if (parsedData.length === 0) {
      setErrors(['No valid data to upload']);
      return;
    }

    setUploading(true);
    try {
      await onSubmit(parsedData, selectedAssessmentId, selectedGroupId);
      handleClose();
    } catch (error: any) {
      setErrors([error.message || 'Failed to upload relationships']);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setErrors([]);
    setUploading(false);
    setSelectedAssessmentId(undefined);
    setSelectedGroupId(undefined);
    setShowNewGroupInput(false);
    setNewGroupName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const downloadTemplate = () => {
    const template = 'employee_name,employee_email,manager_name,manager_email\n' +
                     'John Doe,john.doe@example.com,Jane Smith,jane.smith@example.com\n' +
                     'Alice Johnson,alice.johnson@example.com,Bob Wilson,bob.wilson@example.com';

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'reporting_relationships_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="bulk-upload-modal-overlay" onClick={handleClose}>
      <div className="bulk-upload-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-upload-modal-header">
          <h3>Bulk Upload Reporting Relationships</h3>
          <button className="bulk-upload-modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="bulk-upload-modal-body">
          {/* Instructions */}
          <div className="bulk-upload-instructions">
            <h4>Instructions:</h4>
            <ol>
              <li>Download the CSV template or prepare your own CSV file</li>
              <li>Fill in the required columns: employee_name, employee_email, manager_name, manager_email</li>
              <li>Optionally select an assessment and/or group to assign users to</li>
              <li>Upload the completed CSV file</li>
              <li>Review the parsed data and click "Upload" to create relationships</li>
            </ol>
            <p className="bulk-upload-note">
              <strong>Note:</strong> New users will be automatically onboarded and receive a welcome email with login credentials.
              If an assessment is selected, all users will be assigned to it.
              If a group is selected, all users will be added to that group.
            </p>
          </div>

          {/* Assessment Selection (Optional) */}
          <div className="bulk-upload-option-section">
            <label className="bulk-upload-option-label">
              Assign to Assessment (Optional)
            </label>
            <select
              className="bulk-upload-select"
              value={selectedAssessmentId || ''}
              onChange={(e) => setSelectedAssessmentId(e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={uploading}
            >
              <option value="">-- No Assessment --</option>
              {readyAssessments.map((assessment) => (
                <option key={assessment.assessmentId} value={assessment.assessmentId}>
                  {assessment.assessmentTitle}
                </option>
              ))}
            </select>
          </div>

          {/* Group Selection (Optional) */}
          <div className="bulk-upload-option-section">
            <label className="bulk-upload-option-label">
              Add to Group (Optional)
            </label>
            <select
              className="bulk-upload-select"
              value={showNewGroupInput ? 'new' : (selectedGroupId || '')}
              onChange={(e) => handleGroupChange(e.target.value)}
              disabled={uploading || creatingGroup}
            >
              <option value="">-- No Group --</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} ({group.member_count} members)
                </option>
              ))}
              <option value="new">+ Create New Group</option>
            </select>

            {showNewGroupInput && (
              <div className="bulk-upload-new-group">
                <input
                  type="text"
                  className="bulk-upload-new-group-input"
                  placeholder="Enter group name..."
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  disabled={creatingGroup}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateGroup();
                  }}
                />
                <button
                  className="bulk-upload-btn-create-group"
                  onClick={handleCreateGroup}
                  disabled={!newGroupName.trim() || creatingGroup}
                >
                  {creatingGroup ? 'Creating...' : 'Create'}
                </button>
                <button
                  className="bulk-upload-btn-cancel-group"
                  onClick={() => {
                    setShowNewGroupInput(false);
                    setNewGroupName('');
                  }}
                  disabled={creatingGroup}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Download Template Button */}
          <button
            type="button"
            className="bulk-upload-btn-template"
            onClick={downloadTemplate}
          >
            Download CSV Template
          </button>

          {/* File Upload */}
          <div className="bulk-upload-file-section">
            <label htmlFor="csvFile" className="bulk-upload-file-label">
              Select CSV File *
            </label>
            <input
              ref={fileInputRef}
              id="csvFile"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={uploading}
              className="bulk-upload-file-input"
            />
            {file && (
              <div className="bulk-upload-file-info">
                <span className="bulk-upload-file-name">{file.name}</span>
                <span className="bulk-upload-file-size">
                  ({(file.size / 1024).toFixed(2)} KB)
                </span>
              </div>
            )}
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bulk-upload-errors">
              <h4>Validation Errors:</h4>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Preview Table */}
          {parsedData.length > 0 && (
            <div className="bulk-upload-preview">
              <h4>Preview ({parsedData.length} relationships)</h4>
              <div className="bulk-upload-table-wrapper">
                <table className="bulk-upload-preview-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Employee Name</th>
                      <th>Employee Email</th>
                      <th>Manager Name</th>
                      <th>Manager Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 10).map((row, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{row.employee_name}</td>
                        <td>{row.employee_email}</td>
                        <td>{row.manager_name}</td>
                        <td>{row.manager_email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedData.length > 10 && (
                  <p className="bulk-upload-preview-note">
                    Showing first 10 of {parsedData.length} relationships
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bulk-upload-modal-footer">
          <button
            className="bulk-upload-btn-secondary"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            className="bulk-upload-btn-primary"
            onClick={handleSubmit}
            disabled={uploading || parsedData.length === 0}
          >
            {uploading ? 'Uploading...' : `Upload ${parsedData.length} Relationship${parsedData.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadRelationshipsModal;
